import axios from "axios";
import type { ApiErrorResponse } from "../models/ApiErrorResponse";
import type { AppError } from "../models/AppError";

export function normalizeApiError(error: unknown): AppError {
  if (!axios.isAxiosError(error)) {
    if (isAppError(error)) {
      return error;
    }

    if (error instanceof Error) {
      return {
        title: "Something went wrong",
        message: error.message,
      };
    }

    return {
      title: "Something went wrong",
      message: "An unexpected error occurred.",
    };
  }

  if (!error.response) {
    return {
      title: "Connection error",
      message:
        "PantryStack could not connect to the server. Check your connection and try again.",
    };
  }

  const status = error.response.status;
  const response = error.response.data as (ApiErrorResponse & {title?:string;detail?:string;errors?:Record<string,string[]>}) | undefined;

  return {
    status,
    code: response?.ErrorCode,
    title: response?.Title ?? response?.title ?? getDefaultTitle(status),
    message: response?.Message ?? response?.detail ?? getDefaultMessage(status),
    validationErrors: response?.Errors ?? response?.errors,
  };
}

function isAppError(error: unknown): error is AppError {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  );
}

function getDefaultTitle(status: number): string {
  switch (status) {
    case 400:
      return "Invalid request";
    case 401:
      return "Authentication required";
    case 403:
      return "Access denied";
    case 404:
      return "Not found";
    case 409:
      return "Request conflict";
    case 500:
      return "Server error";
    default:
      return "Request failed";
  }
}

function getDefaultMessage(status: number): string {
  switch (status) {
    case 400:
      return "The submitted information is not valid.";
    case 401:
      return "Your session has expired. Please sign in again.";
    case 403:
      return "You do not have permission to perform this action.";
    case 404:
      return "The requested record could not be found.";
    case 409:
      return "The request conflicts with an existing record.";
    case 500:
      return "The server could not complete the request.";
    default:
      return "The request could not be completed.";
  }
}
